import type { Lead } from '../types/index.ts'
import type { PipelineId } from '../types/ids.ts'
import { NODE_SPECS } from './nodes.ts'
import { rulePathsIn } from './rules.ts'
import { splitPath, templatePathsIn } from './template.ts'
import { isKnownNodeType, NODE_BRANCHES, nodeRole } from './types.ts'
import type { FlowGraph, FlowIssue, FlowIssueCode, FlowNode } from './types.ts'

/**
 * `validateFlowGraph` — everything a graph must satisfy before it is stored
 * (ADR-0007 §6). It runs on every save, in the engine, and the console runs the
 * same function on the draft so a person sees the verdict before pressing it.
 *
 * Directus splits these checks three ways and loses most of them on the way: a
 * DB uniqueness constraint for one, the editor's `isLoop()` for another, a
 * load-time throw for a third — and its REST API validates none of them
 * (study §5, §8). One pure function over one value is what makes "the graph
 * that saved is the graph that runs" true rather than hoped for.
 *
 * **Unreachable nodes are legal.** They never run, they are drawn subdued (D9),
 * and refusing them would make a person delete work in progress to save at all.
 * {@link unreachableNodeIds} is what draws them.
 */

/**
 * The fields `{{ $sale.… }}` may name.
 *
 * **Checked for the same reason `$lead` is, and it was the odd one out** (#194).
 * `SaleSlice` is a closed interface declared in this folder, so `$sale.bogus` is
 * exactly as checkable as `$lead.bogus` — and an unchecked one is not harmless:
 * ADR-0007 §2 reads a missing field as `false`, so a typo saves clean and the
 * rule is silently disarmed for ever. `$event` and `$last` stay unchecked and
 * have a reason to (a trigger projects two event types with different declared
 * fields; `$last` is the previous step's arbitrary return). `$sale` had none.
 */
export const SALE_CHAIN_FIELDS: readonly string[] = ['ludusLeadId', 'openCardsIn']

/** The fields `{{ $lead.… }}` may name — the source card as the chain holds it. */
/**
 * The `$lead` fields that hold a **list**, and may therefore carry one numeric
 * index after them (`$lead.tags[0]`).
 *
 * Declared rather than inferred because the check runs on a path, not on a
 * value: at save time there is no lead to look at. Kept beside
 * {@link LEAD_CHAIN_FIELDS} so a field that becomes a list is one edit.
 */
const LEAD_LIST_FIELDS: readonly string[] = ['tags']

export const LEAD_CHAIN_FIELDS: readonly (keyof Lead)[] = [
  'id',
  'pipelineId',
  'statusId',
  'assignedManagerId',
  'contactId',
  'name',
  'price',
  'funnel',
  'externalRef',
  'tags',
  'createdAt',
  'updatedAt',
  'assignedAt',
]

/** A key names a node; it may not shadow a chain root or hold a path separator. */
const KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_-]*$/

export interface ValidateFlowGraphOptions {
  /**
   * The pipeline the flow belongs to.
   *
   * Only `$sale.openCardsIn.<id>` needs it, and only to accept a rule about the
   * flow's *own* board. ADR-0007 §7 accepts those paths for the pipelines the
   * graph names, because the runtime pre-fills exactly those — and without this
   * the flow's own pipeline is the one board a graph can be about without
   * naming. Optional so `validateFlowGraph(graph)` stays the whole call for
   * every other check.
   */
  pipelineId?: PipelineId
}

const issue = (
  code: FlowIssueCode,
  message: string,
  extra: Omit<FlowIssue, 'code' | 'message'> = {},
): FlowIssue => ({ code, message, ...extra })

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const isInteger = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v)

/* ------------------------------------------------------------ the checker */

export function validateFlowGraph(
  graph: unknown,
  opts: ValidateFlowGraphOptions = {},
): FlowIssue[] {
  if (!isPlainObject(graph) || !Array.isArray(graph.nodes)) {
    return [issue('not_a_graph', 'a graph is an object with a nodes array')]
  }

  const issues: FlowIssue[] = []

  // `retiredKeys` is what holds the never-reuse-a-key invariant up, so a
  // malformed value must be refused rather than filtered: `retiredKeys:
  // "delivery"` is not an array, and silently reading it as none would let
  // `delivery` be minted again — disarming the check while the graph saves
  // clean. `fromGraph` forwards whatever is stored, so it has to be right here.
  const retired = new Set<string>()
  if (graph.retiredKeys !== undefined && graph.retiredKeys !== null) {
    if (!Array.isArray(graph.retiredKeys)) {
      issues.push(
        issue('not_an_array', 'retiredKeys must be an array of keys', { field: 'retiredKeys' }),
      )
    } else {
      for (const key of graph.retiredKeys) {
        if (typeof key !== 'string') {
          issues.push(
            issue('not_a_string', 'a retired key must be a string', { field: 'retiredKeys' }),
          )
          continue
        }
        retired.add(key)
      }
    }
  }

  /* --- 1. each node on its own ------------------------------------------ */

  const nodes: FlowNode[] = []
  const byId = new Map<string, FlowNode>()
  const seenKeys = new Set<string>()

  graph.nodes.forEach((raw, index) => {
    const where = `node ${index}`
    if (!isPlainObject(raw)) {
      issues.push(issue('invalid_node', `${where} is not an object`))
      return
    }
    const id = raw.id
    if (typeof id !== 'string' || id.trim() === '') {
      issues.push(issue('invalid_node', `${where} has no id`))
      return
    }
    if (byId.has(id)) {
      issues.push(issue('duplicate_node_id', `two nodes share the id ${id}`, { nodeId: id }))
      return
    }

    const at = { nodeId: id, key: typeof raw.key === 'string' ? raw.key : undefined }

    const key = raw.key
    if (typeof key !== 'string' || key.trim() === '') {
      issues.push(issue('invalid_key', `${where} has no key`, at))
    } else if (key.startsWith('$')) {
      issues.push(issue('reserved_key', `$ is reserved for the data chain: ${key}`, at))
    } else if (!KEY_PATTERN.test(key)) {
      issues.push(issue('invalid_key', `${key} is not a usable key`, at))
    } else if (seenKeys.has(key)) {
      issues.push(issue('duplicate_key', `two nodes share the key ${key}`, at))
    } else if (retired.has(key)) {
      issues.push(issue('retired_key', `${key} named a node that has been deleted`, at))
    } else {
      seenKeys.add(key)
    }

    const type = raw.type
    if (typeof type !== 'string' || !isKnownNodeType(type)) {
      issues.push(issue('unknown_node_type', `${where} has an unknown type`, at))
    }

    if (!isPlainObject(raw.options)) {
      issues.push(issue('not_an_object', `${where} has no options object`, at))
    }

    for (const branch of NODE_BRANCHES) {
      const target = raw[branch]
      if (target !== null && typeof target !== 'string') {
        issues.push(issue('invalid_node', `${where}'s ${branch} is neither a node id nor null`, at))
      }
    }

    const position = raw.position
    if (!isPlainObject(position) || !isInteger(position.x) || !isInteger(position.y)) {
      // Grid *clamping* is the canvas's (D9); what the stored value must be is
      // this package's, because a fractional cell makes two graphs that draw
      // identically compare unequal.
      issues.push(issue('invalid_position', `${where} needs integer grid coordinates`, at))
    }

    // Keep going with what is usable: a node whose position is wrong still has
    // edges and options worth checking, so a person fixes one save, not four.
    const typed = typeof type === 'string' && isKnownNodeType(type)
    const node: FlowNode = {
      id,
      key: typeof key === 'string' ? key : '',
      // A node whose type this build does not know still takes part in the edge
      // and cycle analysis — it has real pointers — but never reaches a spec it
      // does not have, so this placeholder is never read as a type.
      type: typed ? (type as FlowNode['type']) : 'condition',
      options: isPlainObject(raw.options) ? raw.options : {},
      resolve: typeof raw.resolve === 'string' ? raw.resolve : null,
      reject: typeof raw.reject === 'string' ? raw.reject : null,
      position: isPlainObject(position)
        ? { x: Number(position.x), y: Number(position.y) }
        : { x: 0, y: 0 },
    }
    byId.set(id, node)
    if (typed) nodes.push(node)
  })

  if (byId.size === 0) {
    issues.push(issue('no_trigger', 'a flow needs a trigger'))
    return issues
  }

  /* --- 2. edges, parents, cycles ---------------------------------------- */

  const parents = new Map<string, string[]>()
  for (const node of byId.values()) {
    for (const branch of NODE_BRANCHES) {
      const target = node[branch]
      if (target === null) continue
      const at = { nodeId: node.id, key: node.key, field: branch }
      if (target === node.id) {
        issues.push(issue('edge_self', `${node.key || node.id}'s ${branch} points at itself`, at))
        continue
      }
      if (!byId.has(target)) {
        issues.push(issue('edge_target_missing', `${branch} names no node: ${target}`, at))
        continue
      }
      parents.set(target, [...(parents.get(target) ?? []), node.id])
    }
  }

  for (const [childId, holders] of parents) {
    if (holders.length > 1) {
      const child = byId.get(childId)
      issues.push(
        issue('multiple_parents', `${holders.length} nodes point at ${child?.key ?? childId}`, {
          nodeId: childId,
          key: child?.key,
        }),
      )
    }
  }

  for (const id of findCycle(byId)) {
    const node = byId.get(id)
    issues.push(
      issue('cycle', `${node?.key ?? id} is part of a loop`, { nodeId: id, key: node?.key }),
    )
  }

  /* --- 3. roles: one trigger at the head, at most one action at the tail - */

  const triggers = nodes.filter((n) => nodeRole(n.type) === 'trigger')
  const actions = nodes.filter((n) => nodeRole(n.type) === 'action')

  if (triggers.length === 0) {
    issues.push(issue('no_trigger', 'a flow needs exactly one trigger node'))
  } else if (triggers.length > 1) {
    for (const extra of triggers.slice(1)) {
      issues.push(
        issue('multiple_triggers', 'a flow has exactly one trigger', {
          nodeId: extra.id,
          key: extra.key,
        }),
      )
    }
  }

  for (const trigger of triggers) {
    if ((parents.get(trigger.id) ?? []).length > 0) {
      issues.push(
        issue('trigger_is_targeted', 'nothing may point at the trigger', {
          nodeId: trigger.id,
          key: trigger.key,
        }),
      )
    }
    if (trigger.reject !== null) {
      // A trigger evaluates nothing, so a false branch off it would draw an
      // edge the runtime can never take.
      issues.push(
        issue('trigger_has_reject', 'a trigger has no reject branch', {
          nodeId: trigger.id,
          key: trigger.key,
          field: 'reject',
        }),
      )
    }
  }

  if (actions.length > 1) {
    for (const extra of actions.slice(1)) {
      issues.push(
        issue('multiple_actions', 'a v1 flow carries one action', {
          nodeId: extra.id,
          key: extra.key,
        }),
      )
    }
  }

  for (const action of actions) {
    for (const branch of NODE_BRANCHES) {
      if (action[branch] !== null) {
        // ADR-0007 §5 terminates a run at an action whose effect another run
        // already reserved. That is only well-defined while nothing follows it
        // — otherwise the skip would silently swallow the rest of the chain.
        issues.push(
          issue('action_has_successor', 'nothing follows the action in a v1 flow', {
            nodeId: action.id,
            key: action.key,
            field: branch,
          }),
        )
      }
    }
  }

  /* --- 4. options, per the node's own spec ------------------------------- */

  for (const node of nodes) {
    for (const nodeIssue of NODE_SPECS[node.type].validate(node.options)) {
      issues.push({ ...nodeIssue, nodeId: node.id, key: node.key })
    }
  }

  /* --- 5. what the chain will actually hold ------------------------------ */

  const named = pipelinesNamedBy({ nodes }, opts.pipelineId)
  const keysById = new Map([...byId.values()].map((n) => [n.id, n.key]))

  for (const node of nodes) {
    const upstream = ancestorsOf(node.id, parents, keysById)
    const hasParent = (parents.get(node.id) ?? []).length > 0

    for (const path of templatePathsIn(node.options)) {
      issues.push(...checkPath(path, node, upstream, hasParent, named, seenKeys))
    }
    if (node.type === 'condition') {
      for (const path of rulePathsIn(node.options.rule)) {
        issues.push(...checkPath(path, node, upstream, hasParent, named, seenKeys))
      }
    }
  }

  return issues
}

/* ------------------------------------------------------------- path check */

function checkPath(
  path: string,
  node: FlowNode,
  upstream: ReadonlySet<string>,
  hasParent: boolean,
  namedPipelines: ReadonlySet<string>,
  allKeys: ReadonlySet<string>,
): FlowIssue[] {
  const at = { nodeId: node.id, key: node.key, path }
  const segments = splitPath(path)
  if (segments === null) {
    return [issue('template_unknown_path', `${path} is not a readable path`, at)]
  }

  const [root, ...rest] = segments

  if (root === '$event') return []

  if (root === '$lead') {
    const field = rest[0]
    if (field !== undefined && !(LEAD_CHAIN_FIELDS as readonly string[]).includes(field)) {
      return [issue('template_unknown_path', `a lead has no ${field}`, at)]
    }
    // **A leaf has nothing under it.** A scalar `Lead` field is a leaf, so
    // `$lead.name.bogus` names nothing — and an unchecked trailing segment is
    // not harmless: ADR-0007 §2 reads the missing value as `false`, so the typo
    // saves clean and disarms the rule for ever.
    //
    // **A list field is not a leaf.** `splitPath` reads `$lead.tags[0]` as
    // `['$lead','tags','0']` and `resolvePath` resolves it — `template.test.ts`
    // pins both — so refusing it here made the validator and the renderer
    // disagree about a template the runtime handles correctly. One index, and
    // only after a field that holds a list.
    if (rest.length > 1) {
      const indexable = field !== undefined && LEAD_LIST_FIELDS.includes(field) && rest.length === 2
      if (!indexable || !/^\d+$/.test(rest[1] as string)) {
        return [issue('template_unknown_path', `${field} has no ${rest[1]}`, at)]
      }
    }
    return []
  }

  if (root === '$sale') {
    const field = rest[0]
    if (field !== undefined && !SALE_CHAIN_FIELDS.includes(field)) {
      return [issue('template_unknown_path', `a sale has no ${field}`, at)]
    }
    if (field !== 'openCardsIn') {
      // `ludusLeadId` is a leaf: `$sale.ludusLeadId.anything` names nothing.
      if (rest.length > 1) {
        return [issue('template_unknown_path', `${field} has no ${rest[1]}`, at)]
      }
      return []
    }
    // A count is a leaf too — `openCardsIn.<pipeline>` is where the path ends.
    if (rest.length > 2) {
      return [issue('template_unknown_path', `a card count has no ${rest[2]}`, at)]
    }
    const pipelineId = rest[1]
    // The runtime fills `openCardsIn` for the pipelines the graph names, and
    // only those. A path naming another board would read as absent, and
    // "missing field ⇒ false" would disarm the rule silently — which is the one
    // rule the worked example depends on (ADR-0007 §7).
    if (pipelineId !== undefined && !namedPipelines.has(pipelineId)) {
      return [issue('template_unnamed_pipeline', `this flow names no pipeline ${pipelineId}`, at)]
    }
    return []
  }

  if (root === '$last') {
    if (!hasParent) {
      return [issue('template_not_upstream', 'the first node has no $last', at)]
    }
    return []
  }

  if (root.startsWith('$')) {
    return [issue('template_unknown_root', `${root} is not part of the data chain`, at)]
  }

  if (!allKeys.has(root)) {
    return [issue('template_unknown_root', `no node is called ${root}`, at)]
  }
  if (!upstream.has(root)) {
    // A node can only read what has already run on the path that reached it.
    return [issue('template_not_upstream', `${root} does not run before ${node.key}`, at)]
  }
  return []
}

/* -------------------------------------------------------------- traversal */

/** Every node that can reach `id`, by key. */
function ancestorsOf(
  id: string,
  parents: ReadonlyMap<string, string[]>,
  keysById: ReadonlyMap<string, string>,
): Set<string> {
  const keys = new Set<string>()
  const seen = new Set<string>([id])
  const queue = [...(parents.get(id) ?? [])]
  while (queue.length > 0) {
    const next = queue.shift() as string
    if (seen.has(next)) continue
    seen.add(next)
    const key = keysById.get(next)
    if (key !== undefined && key !== '') keys.add(key)
    queue.push(...(parents.get(next) ?? []))
  }
  return keys
}

/**
 * The ids of every node that sits on a loop, or `[]`.
 *
 * The walk covers the whole node set rather than only what the trigger reaches:
 * an unreachable node is legal, a loop among unreachable nodes is not, and it
 * would become live the moment someone connected it.
 */
function findCycle(byId: ReadonlyMap<string, FlowNode>): string[] {
  const WHITE = 0
  const GREY = 1
  const BLACK = 2
  const colour = new Map<string, number>([...byId.keys()].map((id) => [id, WHITE]))
  const onLoop = new Set<string>()

  const visit = (id: string, stack: string[]): void => {
    colour.set(id, GREY)
    stack.push(id)
    const node = byId.get(id)
    for (const branch of NODE_BRANCHES) {
      const target = node?.[branch]
      if (target === null || target === undefined || !byId.has(target)) continue
      const seen = colour.get(target)
      if (seen === GREY) {
        for (const member of stack.slice(stack.indexOf(target))) onLoop.add(member)
      } else if (seen === WHITE) {
        visit(target, stack)
      }
    }
    stack.pop()
    colour.set(id, BLACK)
  }

  for (const id of byId.keys()) {
    if (colour.get(id) === WHITE) visit(id, [])
  }
  return [...onLoop]
}

/* --------------------------------------------------------------- readings */

/**
 * The one trigger node, or `null`. It is the entry: a run starts here and walks
 * `resolve`/`reject` from it.
 *
 * The entry is *derived* rather than stored as `nodes[0]` or an `entry` field,
 * because a positional entry silently changes meaning when the console
 * reorders an array, and a stored one can disagree with the node it names.
 */
export function entryNodeOf(graph: FlowGraph): FlowNode | null {
  const triggers = graph.nodes.filter(
    (n) => isKnownNodeType(n.type) && nodeRole(n.type) === 'trigger',
  )
  return triggers.length === 1 ? triggers[0] : null
}

/** Nodes the trigger cannot reach. Legal, never run, drawn subdued (D9). */
export function unreachableNodeIds(graph: FlowGraph): string[] {
  const entry = entryNodeOf(graph)
  if (entry === null) return graph.nodes.map((n) => n.id)

  const byId = new Map(graph.nodes.map((n) => [n.id, n]))
  const reached = new Set<string>()
  const queue = [entry.id]
  while (queue.length > 0) {
    const id = queue.shift() as string
    if (reached.has(id)) continue
    reached.add(id)
    const node = byId.get(id)
    for (const branch of NODE_BRANCHES) {
      const target = node?.[branch]
      if (target !== null && target !== undefined && byId.has(target)) queue.push(target)
    }
  }
  return graph.nodes.filter((n) => !reached.has(n.id)).map((n) => n.id)
}

/**
 * The pipelines this graph names: every action node's `targetPipelineId`, plus
 * the flow's own when the caller supplies it. These are the boards the runtime
 * pre-fills `$sale.openCardsIn` for.
 */
export function pipelinesNamedBy(graph: FlowGraph, own?: PipelineId): Set<string> {
  const named = new Set<string>()
  if (own !== undefined) named.add(own)
  for (const node of graph.nodes) {
    if (!isKnownNodeType(node.type) || nodeRole(node.type) !== 'action') continue
    const target = node.options.targetPipelineId
    if (typeof target === 'string') named.add(target)
  }
  return named
}

/**
 * Every `$sale.…` path the graph reads, in templates and in rules.
 *
 * **This has no caller yet, and that absence is the point of saying so.** It
 * exists for the engine's *enable* check, which ADR-0007 §7 requires and which
 * **#165 owes**: before the ludus-lead seam lands `$sale` is
 * `{ ludusLeadId: null, openCardsIn: {} }`, and a rule over it *should* be
 * refused at enable rather than evaluated as false.
 *
 * What happens **today**, with no such check, is the thing the ADR wanted
 * prevented: `evaluateRule` reaches §2's blanket missing-field rule and returns
 * `false`, so the condition takes its `reject` edge and the run ends `done` —
 * the revival rule the worked example turns on, silently disarmed. Exported so
 * that check is one call rather than a second walk of the graph written in the
 * engine; until it has that caller, do not read this comment as describing a
 * guarantee the system holds.
 */
export function salePathsIn(graph: FlowGraph): string[] {
  const paths: string[] = []
  for (const node of graph.nodes) {
    const candidates = [
      ...templatePathsIn(node.options),
      ...(node.type === 'condition' ? rulePathsIn(node.options.rule) : []),
    ]
    for (const path of candidates) {
      if (splitPath(path)?.[0] === '$sale') paths.push(path)
    }
  }
  return paths
}
